import React, { useState, useEffect, useCallback } from 'react';
import { SourceType, Language } from '../../types/enums';
import { NewsSource } from '../../contexts/SourcesContext';
import apiService from '../../services/api';

interface SourceFormProps {
  source?: NewsSource | null;
  onSave: (source: Partial<NewsSource>) => void;
  onCancel: () => void;
}

export const SourceForm: React.FC<SourceFormProps> = ({ source, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: source?.name || '',
    url: source?.url || '',
    sourceType: source?.sourceType || SourceType.RSS,
    language: source?.language || Language.ENGLISH,
    reliabilityScore: source?.reliabilityScore || 5,
    biasScore: source?.biasScore || 5,
    autoProcess: source?.autoProcess ?? true,
    requiresScrapingForRelevance: source?.requiresScrapingForRelevance ?? false,
    requiresScrapingForAnalysis: source?.requiresScrapingForAnalysis ?? true,
    scrapingConfig: source?.scrapingConfig || {
      selectors: ['article', '.article-body', '.content'],
      fallback: 'p',
      timeout: 15000,
    },
    targetCountries: source?.targetCountries || [],
    relevanceThreshold: source?.relevanceThreshold || 0.3,
  });

  
  // RSS validation state
  const [rssValidation, setRssValidation] = useState<{
    status: 'idle' | 'validating' | 'valid' | 'invalid';
    error?: string;
    title?: string;
    description?: string;
  }>({ status: 'idle' });
  const [validationTimeout, setValidationTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Scraping test state
  const [scrapingTest, setScrapingTest] = useState<{
    status: 'idle' | 'testing' | 'success' | 'error';
    result?: any;
  }>({ status: 'idle' });
  
  // State for showing full content
  const [showFullContent, setShowFullContent] = useState(false);

  const availableCountries = [
    { code: 'EST', name: 'Estonia' },
    { code: 'LVA', name: 'Latvia' },
    { code: 'LTU', name: 'Lithuania' },
    { code: 'POL', name: 'Poland' },
    { code: 'UKR', name: 'Ukraine' },
    { code: 'FIN', name: 'Finland' },
    { code: 'USA', name: 'United States' },
    { code: 'RUS', name: 'Russia' },
    { code: 'BLR', name: 'Belarus' },
    { code: 'DEU', name: 'Germany' },
    { code: 'FRA', name: 'France' },
    { code: 'GBR', name: 'United Kingdom' },
  ];

  const sourceTypes = Object.values(SourceType);
  const languages = [
    { code: Language.ENGLISH, name: 'English' },
    { code: Language.ESTONIAN, name: 'Estonian' },
    { code: Language.LATVIAN, name: 'Latvian' },
    { code: Language.LITHUANIAN, name: 'Lithuanian' },
    { code: 'pl', name: 'Polish' },
    { code: 'uk', name: 'Ukrainian' },
    { code: 'ru', name: 'Russian' },
    { code: 'fi', name: 'Finnish' },
    { code: 'de', name: 'German' },
    { code: 'fr', name: 'French' },
  ];

  // RSS validation function
  const validateRSSFeed = useCallback(async (url: string) => {
    if (!url || formData.sourceType !== SourceType.RSS) {
      setRssValidation({ status: 'idle' });
      return;
    }

    try {
      setRssValidation({ status: 'validating' });
      const result = await apiService.validateRSSFeed(url);
      
      if (result.valid) {
        setRssValidation({
          status: 'valid',
          title: result.title,
          description: result.description
        });
        
        // Auto-populate name if empty and RSS feed has a title
        if (!formData.name && result.title) {
          setFormData(prev => ({ ...prev, name: result.title || '' }));
        }
      } else {
        setRssValidation({
          status: 'invalid',
          error: result.error
        });
      }
    } catch (error) {
      setRssValidation({
        status: 'invalid',
        error: 'Failed to validate RSS feed'
      });
    }
  }, [formData.sourceType, formData.name]);

  // Handle URL changes with debounced validation
  const handleUrlChange = (url: string) => {
    setFormData({ ...formData, url });
    
    // Clear previous timeout
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }
    
    // Reset validation state
    setRssValidation({ status: 'idle' });
    
    // Set new timeout for validation (500ms delay)
    if (url && formData.sourceType === SourceType.RSS) {
      const timeout = setTimeout(() => {
        validateRSSFeed(url);
      }, 500);
      setValidationTimeout(timeout);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }
    };
  }, [validationTimeout]);

  // Validate RSS feed on initial load if URL exists
  useEffect(() => {
    if (formData.url && formData.sourceType === SourceType.RSS && rssValidation.status === 'idle') {
      validateRSSFeed(formData.url);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Test scraping function
  const testScraping = async () => {
    if (!formData.url || formData.sourceType !== SourceType.RSS) {
      return;
    }

    setScrapingTest({ status: 'testing' });
    setShowFullContent(false); // Reset view when starting new test

    try {
      const result = await apiService.testScraping(
        formData.url,
        formData.requiresScrapingForRelevance || formData.requiresScrapingForAnalysis
          ? {
              selectors: formData.scrapingConfig.selectors.filter(s => s.trim()),
              fallback: formData.scrapingConfig.fallback,
            }
          : undefined
      );

      setScrapingTest({
        status: result.success ? 'success' : 'error',
        result: result,
      });
    } catch (error) {
      setScrapingTest({
        status: 'error',
        result: { error: 'Failed to test scraping' },
      });
    }
  };

  // Validate CSS selector syntax
  const validateSelector = (selector: string): { valid: boolean; error?: string } => {
    if (!selector.trim()) {
      return { valid: true }; // Empty is valid (will be filtered out)
    }

    try {
      // Basic validation - check for common CSS selector patterns
      const validPatterns = [
        /^[a-zA-Z0-9\-_]+$/, // Element selector (div, article, etc)
        /^\.[a-zA-Z0-9\-_]+/, // Class selector (.class-name)
        /^#[a-zA-Z0-9\-_]+/, // ID selector (#id)
        /^\[[^\]]+\]/, // Attribute selector ([role="main"])
        /^[a-zA-Z0-9\-_\s,>+~:.#[\]="'()]+$/, // Complex selectors
      ];

      const hasValidPattern = validPatterns.some(pattern => pattern.test(selector));
      
      if (!hasValidPattern) {
        return { valid: false, error: 'Invalid CSS selector syntax' };
      }

      // Check for common mistakes
      if (selector.includes('..')) {
        return { valid: false, error: 'Double dots are not valid in CSS selectors' };
      }

      if (selector.endsWith(',')) {
        return { valid: false, error: 'Selector should not end with a comma' };
      }

      return { valid: true };
    } catch {
      return { valid: false, error: 'Invalid selector' };
    }
  };


  const applyTemplate = (templateName: string) => {
    const templates = {
      'csis': {
        name: 'CSIS Think Tank',
        url: 'https://www.csis.org/analysis/feed',
        sourceType: SourceType.RSS,
        language: Language.ENGLISH,
        reliabilityScore: 8,
        biasScore: 6,
        autoProcess: true,
        requiresScrapingForRelevance: false,
        requiresScrapingForAnalysis: true,
        scrapingConfig: {
          selectors: ['.article-content', '.entry-content', 'article'],
          fallback: 'p',
          timeout: 15000,
        },
        targetCountries: ['USA', 'UKR', 'RUS', 'EST', 'LVA', 'LTU'],
        relevanceThreshold: 0.3,
      },
      'isw': {
        name: 'ISW Military Analysis',
        url: 'https://www.understandingwar.org/rss.xml',
        sourceType: SourceType.RSS,
        language: Language.ENGLISH,
        reliabilityScore: 9,
        biasScore: 7,
        autoProcess: true,
        requiresScrapingForRelevance: false,
        requiresScrapingForAnalysis: true,
        scrapingConfig: {
          selectors: ['.field-name-body', '.node-content', 'article'],
          fallback: 'p',
          timeout: 10000,
        },
        targetCountries: ['UKR', 'RUS', 'USA'],
        relevanceThreshold: 0.3,
      },
      'err': {
        name: 'ERR Estonia',
        url: 'https://news.err.ee/rss',
        sourceType: SourceType.RSS,
        language: Language.ENGLISH,
        reliabilityScore: 9,
        biasScore: 5,
        autoProcess: true,
        requiresScrapingForRelevance: false,
        requiresScrapingForAnalysis: true,
        scrapingConfig: {
          selectors: ['.article-content', '.news-content', 'article'],
          fallback: 'p',
          timeout: 10000,
        },
        targetCountries: ['EST'],
        relevanceThreshold: 0.3,
      },
    };

    const template = templates[templateName as keyof typeof templates];
    if (template) {
      setFormData(template);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent submission if RSS validation failed
    if (formData.sourceType === SourceType.RSS && rssValidation.status === 'invalid') {
      alert('Please fix the RSS feed URL before saving.');
      return;
    }
    
    const sourceData = {
      name: formData.name,
      url: formData.url,
      sourceType: formData.sourceType,
      language: formData.language,
      reliabilityScore: formData.reliabilityScore,
      biasScore: formData.biasScore,
      autoProcess: formData.autoProcess,
      requiresScrapingForRelevance: formData.requiresScrapingForRelevance,
      requiresScrapingForAnalysis: formData.requiresScrapingForAnalysis,
      scrapingConfig: (formData.requiresScrapingForRelevance || formData.requiresScrapingForAnalysis) ? {
        selectors: formData.scrapingConfig.selectors.filter(s => s.trim()),
        fallback: formData.scrapingConfig.fallback,
        timeout: formData.scrapingConfig.timeout,
      } : undefined,
      targetCountries: formData.targetCountries,
      relevanceThreshold: formData.relevanceThreshold,
    };

    onSave(sourceData);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold tc-text-primary">
          {source ? 'Edit News Source' : 'Add New Source'}
        </h3>
        <div className="space-x-2">
          <button
            type="button"
            onClick={() => applyTemplate('csis')}
            className="tc-btn tc-btn-secondary text-xs"
          >
            📊 CSIS Template
          </button>
          <button
            type="button"
            onClick={() => applyTemplate('isw')}
            className="tc-btn tc-btn-secondary text-xs"
          >
            🎯 ISW Template
          </button>
          <button
            type="button"
            onClick={() => applyTemplate('err')}
            className="tc-btn tc-btn-secondary text-xs"
          >
            🇪🇪 ERR Template
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium tc-text-primary mb-2">Source Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="tc-input w-full"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium tc-text-primary mb-2">RSS/API URL</label>
            <div className="relative">
              <input
                type="url"
                value={formData.url}
                onChange={(e) => handleUrlChange(e.target.value)}
                className={`tc-input w-full pr-10 ${
                  rssValidation.status === 'valid' ? 'border-green-500' :
                  rssValidation.status === 'invalid' ? 'border-red-500' : ''
                }`}
                placeholder="https://example.com/rss.xml"
                required
              />
              {rssValidation.status === 'validating' && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="tc-spinner w-4 h-4"></div>
                </div>
              )}
              {rssValidation.status === 'valid' && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500">
                  ✓
                </div>
              )}
              {rssValidation.status === 'invalid' && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500">
                  ✗
                </div>
              )}
            </div>
            
            {/* Validation feedback */}
            {rssValidation.status === 'valid' && rssValidation.title && (
              <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-800">
                  <strong>✓ Valid RSS Feed:</strong> {rssValidation.title}
                </p>
                {rssValidation.description && (
                  <p className="text-xs text-green-600 mt-1">{rssValidation.description}</p>
                )}
              </div>
            )}
            
            {rssValidation.status === 'invalid' && rssValidation.error && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">
                  <strong>✗ RSS Validation Failed:</strong> {rssValidation.error}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Source Type and Language */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium tc-text-primary mb-2">Source Type</label>
            <select
              value={formData.sourceType}
              onChange={(e) => setFormData({ ...formData, sourceType: e.target.value as SourceType })}
              className="tc-input w-full"
            >
              {sourceTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium tc-text-primary mb-2">Language</label>
            <select
              value={formData.language}
              onChange={(e) => setFormData({ ...formData, language: e.target.value })}
              className="tc-input w-full"
            >
              {languages.map(lang => (
                <option key={lang.code} value={lang.code}>{lang.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium tc-text-primary mb-2">Reliability Score (1-10)</label>
            <input
              type="number"
              min="1"
              max="10"
              value={formData.reliabilityScore}
              onChange={(e) => setFormData({ ...formData, reliabilityScore: parseInt(e.target.value) })}
              className="tc-input w-full"
            />
          </div>
        </div>

        {/* Auto Process and Bias Score */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium tc-text-primary mb-2">Bias Score (1-10)</label>
            <input
              type="number"
              min="1"
              max="10"
              value={formData.biasScore}
              onChange={(e) => setFormData({ ...formData, biasScore: parseInt(e.target.value) })}
              className="tc-input w-full"
            />
            <p className="text-xs tc-text-tertiary mt-1">
              1 = Very Left, 5 = Neutral, 10 = Very Right
            </p>
          </div>
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.autoProcess}
                onChange={(e) => setFormData({ ...formData, autoProcess: e.target.checked })}
                className="mr-3"
              />
              <div>
                <span className="text-sm font-medium tc-text-primary">Auto Process Articles</span>
                <p className="text-xs tc-text-tertiary mt-1">
                  Automatically send scraped articles to AI analysis
                </p>
              </div>
            </label>
          </div>
          
          {/* Web Scraping Configuration */}
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.requiresScrapingForRelevance}
                onChange={(e) => setFormData({ ...formData, requiresScrapingForRelevance: e.target.checked })}
                className="mr-3"
              />
              <div>
                <span className="text-sm font-medium tc-text-primary">Scrape Full Content for Relevance Check</span>
                <p className="text-xs tc-text-tertiary mt-1">
                  Usually RSS snippets are enough. Enable only if this source has very short snippets.
                </p>
              </div>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.requiresScrapingForAnalysis}
                onChange={(e) => setFormData({ ...formData, requiresScrapingForAnalysis: e.target.checked })}
                className="mr-3"
              />
              <div>
                <span className="text-sm font-medium tc-text-primary">Scrape Full Content for Deep Analysis</span>
                <p className="text-xs tc-text-tertiary mt-1">
                  Enable to scrape full article content for accurate AI analysis (recommended for most sources).
                </p>
              </div>
            </label>
          </div>

          {/* Scraping Selectors (only show if scraping is enabled) */}
          {(formData.requiresScrapingForRelevance || formData.requiresScrapingForAnalysis) && (
            <div className="tc-card p-4 bg-gray-50 border-gray-200 space-y-4">
              <h5 className="font-medium tc-text-primary">Content Scraping Configuration</h5>
              
              <div>
                <label className="block text-sm font-medium tc-text-primary mb-2">
                  CSS Selectors (one per line)
                </label>
                <textarea
                  value={formData.scrapingConfig.selectors.join('\n')}
                  onChange={(e) => setFormData({
                    ...formData,
                    scrapingConfig: {
                      ...formData.scrapingConfig,
                      selectors: e.target.value.split('\n')
                    }
                  })}
                  onKeyDown={(e) => {
                    // Allow Enter key in textarea
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                    }
                  }}
                  className="tc-input w-full h-24 font-mono text-sm"
                  placeholder=".body&#10;.article-content&#10;.story-body&#10;article .content&#10;main article"
                  rows={4}
                />
                <p className="text-xs tc-text-tertiary mt-1">
                  CSS selectors to find article content. First match will be used.
                </p>
                
                {/* Selector validation feedback */}
                {formData.scrapingConfig.selectors.filter(s => s.trim()).map((selector, index) => {
                  const validation = validateSelector(selector);
                  if (!validation.valid) {
                    return (
                      <p key={index} className="text-xs text-red-600 mt-1">
                        Line {index + 1}: {validation.error}
                      </p>
                    );
                  }
                  return null;
                })}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium tc-text-primary mb-2">
                    Fallback Selector
                  </label>
                  <input
                    type="text"
                    value={formData.scrapingConfig.fallback || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      scrapingConfig: {
                        ...formData.scrapingConfig,
                        fallback: e.target.value
                      }
                    })}
                    className="tc-input w-full font-mono text-sm"
                    placeholder="p"
                  />
                  <p className="text-xs tc-text-tertiary mt-1">
                    Used if main selectors fail
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium tc-text-primary mb-2">
                    Timeout (ms)
                  </label>
                  <input
                    type="number"
                    value={formData.scrapingConfig.timeout || 15000}
                    onChange={(e) => setFormData({
                      ...formData,
                      scrapingConfig: {
                        ...formData.scrapingConfig,
                        timeout: parseInt(e.target.value) || 15000
                      }
                    })}
                    className="tc-input w-full"
                    min="5000"
                    max="60000"
                    step="1000"
                  />
                  <p className="text-xs tc-text-tertiary mt-1">
                    Page load timeout
                  </p>
                </div>
              </div>

              <div className="tc-card p-3 bg-yellow-50 border-yellow-200 space-y-2">
                <p className="text-sm text-yellow-800">
                  <strong>CSS Selector Tips:</strong>
                </p>
                <ul className="text-xs text-yellow-700 space-y-1 ml-4">
                  <li>• Use simple class selectors: <code>.body</code>, <code>.article-content</code></li>
                  <li>• Avoid Chrome's "Copy selector" - it's too specific and brittle</li>
                  <li>• Multiple matches are OK - the scraper combines all text</li>
                  <li>• Test in Console: <code>document.querySelectorAll('.body')</code></li>
                  <li>• Common patterns: <code>article</code>, <code>[role="main"]</code>, <code>.content</code></li>
                  <li>• If a selector finds 10+ elements, it might include navigation - be more specific</li>
                </ul>
              </div>

              {/* Test Scraping Button */}
              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={testScraping}
                  disabled={scrapingTest.status === 'testing' || !formData.url || rssValidation.status !== 'valid'}
                  className="tc-btn tc-btn-secondary"
                  title={
                    !formData.url
                      ? 'Please enter a URL first'
                      : rssValidation.status !== 'valid'
                      ? 'Please wait for RSS feed validation to complete successfully'
                      : 'Test the scraping configuration with a sample article from this RSS feed'
                  }
                >
                  {scrapingTest.status === 'testing' ? (
                    <>
                      <div className="tc-spinner w-4 h-4 mr-2"></div>
                      Testing Scraping...
                    </>
                  ) : (
                    '🧪 Test Scraping Configuration'
                  )}
                </button>
              </div>

              {/* Disabled button hint */}
              {(scrapingTest.status !== 'testing' && (!formData.url || rssValidation.status !== 'valid')) && (
                <p className="text-xs text-yellow-600 mt-1">
                  {!formData.url
                    ? '⚠️ Enter an RSS feed URL above to enable testing'
                    : rssValidation.status === 'validating'
                    ? '⏳ Validating RSS feed...'
                    : rssValidation.status === 'invalid'
                    ? '❌ Fix RSS validation errors above to enable testing'
                    : '⚠️ Wait for RSS feed validation to complete'}
                </p>
              )}

              {/* Test Results */}
              {scrapingTest.result && (
                <div className={`tc-card p-4 ${
                  scrapingTest.status === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                }`}>
                  {scrapingTest.status === 'error' ? (
                    <div>
                      <p className="text-sm font-medium text-red-800 mb-2">❌ Test Failed</p>
                      <p className="text-sm text-red-700">{scrapingTest.result.error}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-green-800">✅ Test Results</p>
                      
                      {/* Feed Info */}
                      <div>
                        <p className="text-xs font-semibold text-green-700">RSS Feed:</p>
                        <p className="text-xs text-green-600">
                          {scrapingTest.result.feedInfo?.title} ({scrapingTest.result.feedInfo?.articleCount} articles)
                        </p>
                      </div>

                      {/* First Article Info */}
                      <div>
                        <p className="text-xs font-semibold text-green-700">Test Article:</p>
                        <p className="text-xs text-green-600">{scrapingTest.result.firstArticle?.title}</p>
                        <div className="text-xs mt-1">
                          <div className="flex items-center gap-2 p-2 bg-blue-50 rounded border border-blue-200">
                            <span className="text-gray-600">URL:</span>
                            <a 
                              href={scrapingTest.result.firstArticle?.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline flex-1 break-all"
                            >
                              {scrapingTest.result.firstArticle?.url} 
                              <span className="ml-1 text-gray-500">↗️</span>
                            </a>
                            <button
                              type="button"
                              onClick={() => navigator.clipboard.writeText(scrapingTest.result.firstArticle?.url || '')}
                              className="text-gray-500 hover:text-gray-700 flex-shrink-0"
                              title="Copy URL to clipboard"
                            >
                              📋
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            💡 Open this URL in your browser to compare with the extracted content below
                          </p>
                        </div>
                        <p className="text-xs text-green-600">
                          RSS Content: {scrapingTest.result.firstArticle?.rssContentLength} chars
                        </p>
                      </div>

                      {/* Scraping Results */}
                      {scrapingTest.result.scrapingTest && (
                        <div>
                          <p className="text-xs font-semibold text-green-700">Scraping Test:</p>
                          {scrapingTest.result.scrapingTest.success ? (
                            <>
                              <p className="text-xs text-green-600">
                                ✅ Scraped {scrapingTest.result.scrapingTest.scrapedContentLength} characters
                              </p>
                              
                              {/* Selector Results */}
                              {scrapingTest.result.scrapingTest.selectorResults && (
                                <div className="mt-2 space-y-1">
                                  <p className="text-xs font-semibold text-green-700">Selector Results:</p>
                                  {Object.entries(scrapingTest.result.scrapingTest.selectorResults).map(([selector, result]: [string, any]) => (
                                    <div key={selector} className="text-xs">
                                      <code className="bg-gray-100 px-1 py-0.5 rounded">{selector}</code>:
                                      {result.error ? (
                                        <span className="text-red-600 ml-2">❌ {result.error}</span>
                                      ) : result.found > 0 ? (
                                        <span className="text-green-600 ml-2">
                                          ✅ Found {result.found} element{result.found !== 1 ? 's' : ''}
                                        </span>
                                      ) : (
                                        <span className="text-yellow-600 ml-2">⚠️ No matches</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Content Preview */}
                              {scrapingTest.result.scrapingTest.scrapedContentPreview && (
                                <div className="mt-2">
                                  <div className="flex items-center justify-between">
                                    <p className="text-xs font-semibold text-green-700">Content Preview:</p>
                                    {scrapingTest.result.scrapingTest.fullContent && (
                                      <button
                                        type="button"
                                        onClick={() => setShowFullContent(!showFullContent)}
                                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                                      >
                                        {showFullContent ? '📄 Show Preview' : '📖 View Full Content'}
                                      </button>
                                    )}
                                  </div>
                                  <div className="text-xs text-green-600 bg-white p-2 rounded border border-green-200 mt-1 whitespace-pre-wrap break-words max-h-96 overflow-y-auto">
                                    {showFullContent && scrapingTest.result.scrapingTest.fullContent
                                      ? scrapingTest.result.scrapingTest.fullContent
                                      : `${scrapingTest.result.scrapingTest.scrapedContentPreview}...`}
                                  </div>
                                  {showFullContent && scrapingTest.result.scrapingTest.fullContent && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      💡 Full content ({scrapingTest.result.scrapingTest.scrapedContentLength} chars) - This is what AI will analyze. Not saved to database.
                                    </p>
                                  )}
                                </div>
                              )}
                            </>
                          ) : (
                            <p className="text-xs text-red-600">❌ Failed to scrape content</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Target Countries */}
        <div>
          <label className="block text-sm font-medium tc-text-primary mb-2">Target Countries</label>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {availableCountries.map(country => (
              <label key={country.code} className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={formData.targetCountries.includes(country.code)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData({
                        ...formData,
                        targetCountries: [...formData.targetCountries, country.code],
                      });
                    } else {
                      setFormData({
                        ...formData,
                        targetCountries: formData.targetCountries.filter(c => c !== country.code),
                      });
                    }
                  }}
                  className="mr-2"
                />
                {country.name}
              </label>
            ))}
          </div>
        </div>

        {/* Article Processing Settings */}
        <div className="space-y-4">
          <h4 className="text-lg font-medium tc-text-primary">Article Processing Settings</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Relevance Threshold */}
            <div>
              <label className="block text-sm font-medium tc-text-primary mb-2">
                Relevance Threshold
              </label>
              <input
                type="number"
                value={formData.relevanceThreshold}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  relevanceThreshold: parseFloat(e.target.value) || 0.3 
                })}
                className="tc-input w-full"
                min="0"
                max="1"
                step="0.1"
              />
              <p className="text-xs tc-text-tertiary mt-1">
                Minimum relevance score (0.0-1.0) to analyze article
              </p>
            </div>
          </div>

          <div className="tc-card p-4 bg-blue-50 border-blue-200">
            <h5 className="font-medium text-blue-900 mb-2">How Relevance Filtering Works</h5>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Stage 1: Gemini Flash quickly checks if article is relevant to Baltic security</li>
              <li>• Stage 2: Only relevant articles are fully analyzed with Claude Sonnet</li>
              <li>• This reduces costs by ~90% for high-volume sources like BBC</li>
            </ul>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="tc-btn tc-btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="tc-btn tc-btn-primary"
          >
            {source ? 'Update Source' : 'Create Source'}
          </button>
        </div>
      </form>
    </div>
  );
};